import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "./cityPricing";

const cannes: ChauffeurPage = {
  slug: "cannes",
  title: "Chauffeur privé à Cannes – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Cannes : transferts aéroport, mise à disposition, événements et longue distance, 7j/7.",
  keywords: [
    "chauffeur privé cannes",
    "vtc cannes",
    "chauffeur cannes",
    "vtc cannes aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Cannes",
    subtitle:
      "Transferts aéroport de Nice, mises à disposition, événements et trajets longue distance — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Cannes",
      "chauffeur Croisette",
      "transfert aéroport Nice",
      "chauffeur Côte d’Azur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Cannes",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi nous choisir à Cannes",
    items: [
      {
        title: "Disponible 24h/24, 7j/7",
        description:
          "Transferts et mises à disposition à toute heure, suivi de vol inclus.",
      },
      {
        title: "Flotte premium",
        description:
          "Berline Mercedes Classe E/S, van, SUV, minibus VIP et véhicules de prestige.",
      },
      {
        title: "Chauffeurs bilingues",
        description: "FR/EN, ponctuels et discrets, accueil premium.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Cannes",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et Cannes-Mandelieu.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur privé dédié pour tous vos déplacements.",
      },
      {
        title: "Événements & soirées",
        text: "Festival, MIPIM, Cannes Lions, mariages, galas : logistique VIP au cœur de Cannes.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Cannes : Monaco, Saint-Tropez, Italie, stations de ski.",
      },
      {
        title: "Transfert port & yachting",
        text: "Liaison avec les ports de Cannes, Antibes et Monaco pour équipages et invités.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "La Croisette" },
      { name: "Palais des Festivals" },
      { name: "Le Suquet" },
      { name: "Port Canto & Vieux Port" },
      { name: "Gare de Cannes" },
      { name: "Aéroport de Nice" },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Cannes",
    subheading:
      "Du transfert aéroport quotidien aux grands événements, un service premium constant.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Cannes et sur toute la Côte d’Azur : transferts aéroport de Nice, rendez-vous d’affaires, mises à disposition à l’heure ou à la journée, et trajets longue distance.",
      "Nos chauffeurs bilingues, ponctuels et discrets, connaissent parfaitement Cannes — la Croisette, le Palais des Festivals, les hôtels et les ports — pour vous faire gagner un temps précieux, même en pleine affluence.",
      "Pendant les grands événements (Festival de Cannes, MIPIM, Cannes Lions, Yachting Festival), nous coordonnons des flottes multi-véhicules avec un pilotage centralisé pour des rythmes intenses.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Flotte premium adaptée à chaque besoin",
      "Coordination multi-véhicules pour les événements",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Cannes",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Cannes", "CANNES"),
    {
      q: "Assurez-vous les transferts depuis l’aéroport de Nice ?",
      a: "Oui, 24h/24 avec suivi de vol et accueil personnalisé, depuis et vers l’aéroport de Nice Côte d’Azur et Cannes-Mandelieu.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les événements ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Cannes ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV, minibus VIP et véhicules de prestige, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Intervenez-vous pendant le Festival de Cannes ?",
      a: "Oui, nous opérons régulièrement pendant le Festival, le MIPIM, le Cannes Lions et les salons, avec organisation dédiée et flottes coordonnées.",
    },
    {
      q: "Proposez-vous un chauffeur pour mariage ?",
      a: "Oui, voiture haut de gamme, chauffeur en tenue et coordination des navettes invités pour votre mariage sur la Côte d’Azur.",
    },
    {
      q: "Faites-vous les longues distances depuis Cannes ?",
      a: "Oui, trajets toute distance : Monaco, Saint-Tropez, Marseille, Italie et stations de ski, avec un véhicule confortable et un chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur pendant le Festival de Cannes",
      href: "/chauffeur/festival-de-cannes",
    },
    {
      label: "Cannes ⇄ Monaco, tarif forfaitaire dès 200 €",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Cannes ⇄ Saint-Tropez, dès 250 €",
      href: "/chauffeur/transferts/cannes-saint-tropez",
    },
    {
      label: "Cannes vers Aéroport de Nice avec chauffeur",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Berline avec chauffeur (Classe E)",
      href: "/chauffeur/berline-avec-chauffeur",
    },
    { label: "Chauffeur pour événements", href: "/chauffeur/evenements" },
    {
      label: "Louer une voiture Cannes",
      href: "/location/location-voiture-cannes",
    },
    {
      label: "Voiture avec chauffeur pour un mariage",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Cannes",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("fr", "Cannes", "CANNES", ", VTC 24h/24"),
    canonical: "/chauffeur/cannes",
  },
};

export default cannes;
